import {paraphraseRequest} from '@apis/paraphrase'
import {Box, Flex, Text} from '@chakra-ui/react'
import Loading from '@components/Loading'
import {
  updateFooterData,
  updateIsLoading,
  updateOriginSentence,
} from '@redux/paraphrase/paraphraseSlice'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'

// Định nghĩa types
interface HighlightItem {
  content_words_order?: number
  words: string
}

interface ColorScheme {
  color: string
  bg: string
}

// Component hiển thị highlight items
const HighlightItems = ({
  items,
  colors,
  fontSize,
}: {
  items: HighlightItem[]
  colors: ColorScheme[]
  fontSize: string
}) => (
  <Flex
    flexWrap={'wrap'}
    p={{base: '16px', md: '16px'}}
    gap={{base: '8px', md: '16px'}}
    justifyContent={'center'}
    alignItems={'center'}
    {...(fontSize === '24px' && {bg: '#EEF8FF', borderRadius: '16px'})}>
    {items?.map((item, index) => {
      const colorScheme = item.content_words_order
        ? colors[item.content_words_order - 1]
        : null

      return (
        <Text
          key={index}
          color={colorScheme?.color || '#101828'}
          bg={colorScheme?.bg || 'transparent'}
          p={colorScheme ? {base: '2px 8px', md: '4px 12px'} : '0'}
          border={colorScheme ? `1px solid ${colorScheme.color}` : 'none'}
          fontWeight={colorScheme ? '600' : '400'}
          fontSize={{base: fontSize === '24px' ? '18px' : '14px', md: fontSize}}
          borderRadius={'8px'}
          whiteSpace={'nowrap'}>
          {item.words}
        </Text>
      )
    })}
  </Flex>
)

const YourSentence = () => {
  const {
    currentSentence,
    sessionId,
    colorsParaphrase,
    originSentence,
    isCorrectGrammars,
  } = useSelector((state: any) => state.paraphrase)
  const [isLoading, setIsLoading] = useState(true)
  const [correctGrammar, setCorrectGrammar] = useState(true)
  const dispatch = useDispatch()

  useEffect(() => {
    const getParaphrase = async () => {
      if (!currentSentence?.text_en || !sessionId) {
        setIsLoading(false)
        return
      }

      try {
        dispatch(updateIsLoading(true))
        let sentence

        if (isCorrectGrammars) {
          const resCorrectGrammars = await paraphraseRequest.correctGrammars({
            sessionId,
            sentence: currentSentence.text_en,
          })
          setCorrectGrammar(resCorrectGrammars?.data?.data?.correct_grammar)
          sentence = resCorrectGrammars?.data?.data?.fixed_sentence
        } else {
          sentence = currentSentence.text_en
        }

        const res = await paraphraseRequest.getParaphrase({
          sessionId,
          sentence,
        })
        dispatch(updateOriginSentence(res?.data?.data))
        const footerData =
          res?.data?.data?.highlight_items_en
            ?.filter((item: any) => item.content_words_order)
            ?.map((item: any) => item) || []
        dispatch(updateFooterData(footerData))
      } catch (error) {
        console.error('Failed to fetch paraphrase:', error)
      } finally {
        setIsLoading(false)
        dispatch(updateIsLoading(false))
      }
    }

    setIsLoading(true)
    getParaphrase()
  }, [currentSentence, sessionId])

  return (
    <Flex
      flexDirection={'column'}
      bg={'#fff'}
      p={{base: '16px', md: '32px'}}
      borderRadius={'24px'}
      h={'100%'}
      position={'relative'}>
      {isLoading && <Loading />}
      <Text
        fontSize={{base: '20px', md: '24px'}}
        fontWeight={'600'}
        textAlign={'center'}
        mt={{base: '16px', md: '32px'}}>
        Câu của bạn
      </Text>

      <Flex
        flexDirection={'column'}
        flex={1}
        justifyContent={'center'}
        mt={{base: '-50px', md: '-100px'}}>
        {originSentence?.highlight_items_en && (
          <Box mt={{base: '20px', md: '40px'}}>
            <HighlightItems
              items={originSentence.highlight_items_en}
              colors={colorsParaphrase}
              fontSize={'24px'}
            />
          </Box>
        )}

        {originSentence?.highlight_items_vi && (
          <Box mt={{base: '20px', md: '40px'}}>
            <HighlightItems
              items={originSentence.highlight_items_vi}
              colors={colorsParaphrase}
              fontSize={'16px'}
            />
          </Box>
        )}
      </Flex>
      {!correctGrammar && !isLoading && (
        <Text
          p={'16px'}
          px={'32px'}
          bg={'#FFF9ED'}
          border={'1px solid #FA8C16'}
          fontSize={'14px'}
          mt={'16px'}
          w={'fit-content'}
          borderRadius={{base: '20px', md: '99px'}}>
          Câu bạn nhập hình như đã có chút nhầm lẫn ngữ pháp, tụi mình đã sửa
          lại trước khi chúng ta luyện tập Paraphase bạn nhé!
        </Text>
      )}
    </Flex>
  )
}

export default YourSentence
